import React from 'react';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';

/**
 * Reusable error display component
 * @param {Object} props
 * @param {string} props.error - Error message to display
 * @param {string} props.title - Optional error title
 * @param {Function} props.onRetry - Optional retry callback
 * @param {string} props.retryText - Text for retry button
 * @param {Function} props.onDismiss - Optional dismiss callback
 * @param {boolean} props.fullScreen - Whether to take full screen
 * @param {string} props.className - Additional CSS classes
 */
export default function ErrorDisplay({ 
  error, 
  title = 'Something went wrong',
  onRetry,
  retryText = 'Try again',
  onDismiss,
  fullScreen = false,
  className = ''
}) {
  const content = (
    <div className="text-center">
      <div className="mb-4 flex justify-center">
        <div className="h-12 w-12 rounded-full bg-red-500/20 flex items-center justify-center">
          <ExclamationTriangleIcon className="h-6 w-6 text-red-400" />
        </div>
      </div>
      
      <h3 className="text-lg font-semibold text-white mb-2">{title}</h3>
      
      {error && (
        <p className="text-red-400 mb-4 text-sm">{error}</p>
      )}
      
      <div className="flex gap-3 justify-center">
        {onRetry && (
          <button
            onClick={onRetry}
            className="px-4 py-2 bg-primary hover:bg-primary/90 text-white rounded-md text-sm font-medium transition-colors"
          >
            {retryText}
          </button>
        )}
        
        {onDismiss && (
          <button
            onClick={onDismiss}
            className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-md text-sm font-medium transition-colors"
          >
            Dismiss
          </button>
        )}
      </div>
    </div>
  );

  if (fullScreen) {
    return (
      <div className={`min-h-screen flex items-center justify-center bg-black text-white px-4 ${className}`}>
        <div className="max-w-md w-full">
          {content}
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-gray-900 rounded-lg p-6 border border-gray-700 ${className}`}>
      {content}
    </div>
  );
} 